import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MessageBar, MessageBarBody, MessageBarTitle, Spinner } from '@fluentui/react-components';
import { useParams, useSearchParams } from 'react-router-dom';
import type { Chart, PublicDashboard } from '../api/types';
import { publicDashboardsService } from '../api/publicDashboardsService';
import { sharedDashboardsService } from '../api/sharedDashboardsService';
import { DashboardFilterBar } from '../components/DashboardFilterBar';
import { DashboardGridLayout } from '../components/grid';
import { chartsToLayout } from '../components/grid/layoutUtils';
import { DashboardAccessModeProvider, type DashboardAccessMode } from '../components/security/WorkspaceAccess';
import { flattenFilterValues } from '../hooks/filterStore';
import { useTheme } from '../contexts/ThemeContext';
import { mergeDashboardSettings } from '../components/DashboardSettingsPanel';
import { useTopBarBreadcrumbs, useTopBarTitle } from '../contexts/TopBarContext';
import '../components/Dashboard.css';
import './SharedDashboardPage.css';

interface SharedDashboardPageProps {
  mode: Exclude<DashboardAccessMode, 'owner'>;
}

const SharedDashboardPage: React.FC<SharedDashboardPageProps> = ({ mode }) => {
  const { token = '', id = '' } = useParams<{ token: string; id: string }>();
  const [searchParams] = useSearchParams();
  const { isDarkMode } = useTheme();
  const [dashboard, setDashboard] = useState<PublicDashboard | null>(null);
  const [error, setError] = useState(false);
  const [filterValues, setFilterValues] = useState<Map<string, any>>(new Map());
  const [defaults, setDefaults] = useState<Record<string, unknown>>({});
  const [readyFilters, setReadyFilters] = useState<Set<string>>(new Set());

  const topBarTitle = mode === 'shared-readonly' ? dashboard?.name ?? 'Shared Dashboard' : '';
  const showEmbedHeader = mode === 'embedded-readonly' && searchParams.get('showHeader') !== 'false';
  const showFilters = mode !== 'embedded-readonly' || searchParams.get('showFilters') !== 'false';
  useTopBarTitle(topBarTitle);
  useTopBarBreadcrumbs(mode === 'shared-readonly' ? [
    { text: 'Shared Dashboards', to: '/shared-dashboards' },
    { text: dashboard?.name ?? 'Shared Dashboard' },
  ] : []);

  useEffect(() => {
    let cancelled = false;
    setDashboard(null);
    setError(false);
    setFilterValues(new Map());
    setDefaults({});
    setReadyFilters(new Set());
    const request = mode === 'embedded-readonly'
      ? publicDashboardsService.getDashboard(token)
      : sharedDashboardsService.getDashboard(id);
    request
      .then((value) => { if (!cancelled) setDashboard(value); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [id, mode, token]);

  const charts = (dashboard?.charts ?? []) as unknown as Chart[];
  const settings = mergeDashboardSettings(dashboard?.settings);
  const filtersObject = useMemo(() => {
    const values = { ...defaults, ...Object.fromEntries(filterValues) };
    return flattenFilterValues(values);
  }, [defaults, filterValues]);
  const filtersReady = !showFilters || (dashboard?.filters ?? []).every((filter) => readyFilters.has(filter.id));
  const onFilterReady = useCallback((id: string) => setReadyFilters((current) => new Set(current).add(id)), []);

  if (error) {
    return <div className="shared-dashboard-state"><MessageBar intent="error"><MessageBarBody><MessageBarTitle>Dashboard unavailable</MessageBarTitle>The link is invalid, expired, or revoked.</MessageBarBody></MessageBar></div>;
  }
  if (!dashboard) return <div className="shared-dashboard-state"><Spinner size="large" label="Loading dashboard" /></div>;

  return (
    <DashboardAccessModeProvider mode={mode}>
      <main className={`shared-dashboard ${mode === 'embedded-readonly' ? 'shared-dashboard--embedded' : ''}`}>
        {showEmbedHeader && (
          <header className="shared-dashboard__header">
            <h1>{dashboard.name}</h1>
          </header>
        )}
        {showFilters && dashboard.filters.length > 0 && (
          <DashboardFilterBar
            dashboardId={dashboard.id}
            filters={dashboard.filters}
            filterValues={filterValues}
            onFilterChange={(key, value) => setFilterValues((current) => {
              const next = new Map(current);
              if (value == null || (Array.isArray(value) && value.length === 0)) next.delete(key); else next.set(key, value);
              return next;
            })}
            onFilterDefault={(key, value) => setDefaults((current) => ({ ...current, [key]: value }))}
            onFilterReset={(key) => setFilterValues((current) => { const next = new Map(current); next.delete(key); return next; })}
            onResetAll={() => setFilterValues(new Map())}
            onFilterReady={onFilterReady}
            shareToken={mode === 'embedded-readonly' ? token : undefined}
            sharedDashboardId={mode === 'shared-readonly' ? id : undefined}
          />
        )}
        <div className="shared-dashboard__content">
          <DashboardGridLayout
            dashboardId={dashboard.id}
            charts={charts}
            layout={chartsToLayout(charts)}
            onLayoutChange={() => undefined}
            filters={filtersObject}
            filtersReady={filtersReady}
            isDark={isDarkMode}
            dashboardTheme={settings.theme}
            animated={settings.animations}
            shareToken={mode === 'embedded-readonly' ? token : undefined}
            sharedDashboardId={mode === 'shared-readonly' ? id : undefined}
          />
        </div>
      </main>
    </DashboardAccessModeProvider>
  );
};

export default SharedDashboardPage;